import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bot, Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabase";

/**
 * KPI admin : usage réel du chat IA vs de l'éditeur manuel, ventilé par
 * produit et par plan utilisateur (anonyme/free/payant). Source : RPC
 * `get_ai_usage_metrics` (admin-only, agrège `analytics_events`).
 *
 * Couvre les 4 events posés par UsageAnalyticsTracker : chat_message_sent,
 * ai_generation_applied, manual_edit, poll_reopened_for_edit.
 */

type AiUsageEventType =
  | "chat_message_sent"
  | "ai_generation_applied"
  | "manual_edit"
  | "poll_reopened_for_edit";

interface AiUsageMetricRow {
  event_type: AiUsageEventType | string;
  product: string;
  plan_type: string;
  count_24h: number;
  count_7d: number;
  count_30d: number;
  count_90d: number;
  count_lifetime: number;
}

const EVENT_ORDER: AiUsageEventType[] = [
  "chat_message_sent",
  "ai_generation_applied",
  "manual_edit",
  "poll_reopened_for_edit",
];

export const AiUsageAnalyticsKPI: React.FC = () => {
  const { t } = useTranslation("admin");
  const [rows, setRows] = useState<AiUsageMetricRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error: rpcError } = await supabase.rpc(
        "get_ai_usage_metrics",
      );
      if (!active) return;
      if (rpcError) {
        setError(rpcError.message);
      } else {
        setRows((data as AiUsageMetricRow[] | null) ?? []);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const sortedRows = rows
    ? [...rows].sort((a, b) => {
        const orderDiff =
          EVENT_ORDER.indexOf(a.event_type as AiUsageEventType) -
          EVENT_ORDER.indexOf(b.event_type as AiUsageEventType);
        if (orderDiff !== 0) return orderDiff;
        if (a.product !== b.product) return a.product.localeCompare(b.product);
        return a.plan_type.localeCompare(b.plan_type);
      })
    : [];

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-400" />
          {t("ai_usage.title")}
        </h3>
      </div>
      <p className="text-xs text-white/40 mb-4">{t("ai_usage.subtitle")}</p>

      {loading && (
        <div className="flex items-center gap-2 text-white/60 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("ai_usage.loading")}
        </div>
      )}

      {error && !loading && (
        <div className="text-red-300 text-sm">
          {t("ai_usage.error", { message: error })}
        </div>
      )}

      {!loading && !error && sortedRows.length === 0 && (
        <div className="text-white/60 text-sm">{t("ai_usage.no_data")}</div>
      )}

      {!loading && !error && sortedRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 border-b border-white/10">
                <th className="py-2 pr-4 font-medium">
                  {t("ai_usage.table.event")}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {t("ai_usage.table.product")}
                </th>
                <th className="py-2 pr-4 font-medium">
                  {t("ai_usage.table.plan")}
                </th>
                <th className="py-2 pr-4 font-medium text-right">
                  {t("ai_usage.table.count_24h")}
                </th>
                <th className="py-2 pr-4 font-medium text-right">
                  {t("ai_usage.table.count_7d")}
                </th>
                <th className="py-2 pr-4 font-medium text-right">
                  {t("ai_usage.table.count_30d")}
                </th>
                <th className="py-2 pr-4 font-medium text-right">
                  {t("ai_usage.table.count_90d")}
                </th>
                <th className="py-2 font-medium text-right">
                  {t("ai_usage.table.count_lifetime")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => (
                <tr
                  key={`${row.event_type}-${row.product}-${row.plan_type}-${i}`}
                  className="border-b border-white/5 text-white/80"
                >
                  <td className="py-2 pr-4">
                    {t(`ai_usage.events.${row.event_type}`, {
                      defaultValue: row.event_type,
                    })}
                  </td>
                  <td className="py-2 pr-4 text-white/60">{row.product}</td>
                  <td className="py-2 pr-4 text-white/60">{row.plan_type}</td>
                  <td className="py-2 pr-4 text-right font-mono">
                    {row.count_24h}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono">
                    {row.count_7d}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono">
                    {row.count_30d}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono">
                    {row.count_90d}
                  </td>
                  <td className="py-2 text-right font-mono">
                    {row.count_lifetime}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AiUsageAnalyticsKPI;
